import { Box } from '@mui/material';
import type { AnimeatableIconRef } from '@parkdna/ui/icons/types';
import { useTheme } from '@parkdna/ui/theme';
import type { LucideIcon } from 'lucide-react';
import type { ForwardRefExoticComponent, RefAttributes } from 'react';
import { forwardRef, useImperativeHandle, useRef } from 'react';

import TooltipIcon from './TooltipIcon';

type AnimatedIconComponent = ForwardRefExoticComponent<
  { size?: number; strokeWidth?: number } & RefAttributes<AnimeatableIconRef>
>;

type IconComponent = AnimatedIconComponent | LucideIcon;

function isLucideIcon(icon: IconComponent): icon is LucideIcon {
  // Lucide icons have displayName starting with the icon name
  // and don't have our specific animated icon pattern
  return !('$$typeof' in icon && icon.displayName?.endsWith('Icon'));
}

export type MenuIconRef = {
  startAnimation: () => void;
  stopAnimation: () => void;
};

export type MenuIconProps = {
  Icon: IconComponent;
  title: string;
  shouldShowTooltip: boolean;
  size?: number;
  marginRight?: number;
};

const MenuIcon = forwardRef<MenuIconRef, MenuIconProps>(
  ({ Icon, title, shouldShowTooltip, size = 18, marginRight = 1 }, ref) => {
    const [theme] = useTheme();
    const animatedIconRef = useRef<AnimeatableIconRef>(null);

    useImperativeHandle(ref, () => ({
      startAnimation: () => {
        void animatedIconRef.current?.startAnimation();
      },
      stopAnimation: () => {
        void animatedIconRef.current?.stopAnimation();
      },
    }));

    const iconContent = (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          verticalAlign: 'middle',
          color: theme.palette.primary.main,
          '& svg': { width: size, height: size },
          '& svg path': { fill: 'none' },
          mr: marginRight,
        }}
      >
        {isLucideIcon(Icon) ? (
          <Icon
            size={size}
            strokeWidth={1.5}
            color={theme.palette.primary.main}
            fill="none"
            stroke="currentColor"
          />
        ) : (
          <Icon ref={animatedIconRef} size={size} strokeWidth={1.5} />
        )}
      </Box>
    );

    return (
      <TooltipIcon title={title} shouldShowTooltip={shouldShowTooltip}>
        {iconContent}
      </TooltipIcon>
    );
  }
);

MenuIcon.displayName = 'MenuIcon';

export default MenuIcon;
